import type { NewDeal, Product } from 'types'

// ─── Types ────────────────────────────────────────────────────────────

export type AgreementFamily = 'MSA' | 'MSA - Housing' | 'CSA'
export type VersionChangeType = 'major' | 'minor' | 'patch'

export interface VersionedDeal {
  deal: NewDeal
  semanticVersion: string
  masterAgreementId: string
  isRoot: boolean
  products: Product[]
}

export interface MasterAgreement {
  masterAgreementId: string
  rootDeal: NewDeal
  agreementFamily: AgreementFamily
  currentVersion: VersionedDeal
  versions: VersionedDeal[]
}

// ─── Constants ────────────────────────────────────────────────────────
// Deal types are stored as short codes (e.g. "MSA", "RMSA")
// via renderDealType() in DealTypeSelect.tsx

const MSA_FAMILY_TYPES = new Set(['MSA', 'RMSA'])
const MSA_HOUSING_FAMILY_TYPES = new Set(['MSA - Housing', 'RMSA - Housing'])
const CSA_FAMILY_TYPES = new Set(['CSA', 'RCSA'])

const ROOT_TYPES: Record<AgreementFamily, Set<string>> = {
  MSA: new Set(['MSA']),
  'MSA - Housing': new Set(['MSA - Housing']),
  CSA: new Set(['CSA']),
}

const VERSIONABLE_TYPES = new Set([...MSA_FAMILY_TYPES, ...MSA_HOUSING_FAMILY_TYPES, ...CSA_FAMILY_TYPES])

const TERMINAL_STATUSES = new Set(['Ended', 'Cancelled', 'Replaced'])

// ─── Helpers ──────────────────────────────────────────────────────────

export function isVersionableDeal(deal: NewDeal): boolean {
  return VERSIONABLE_TYPES.has(deal?.type ?? '')
}

export function isRootType(type: string): boolean {
  return ROOT_TYPES.MSA.has(type) || ROOT_TYPES['MSA - Housing'].has(type) || ROOT_TYPES.CSA.has(type)
}

export function getAgreementFamily(type: string): AgreementFamily | null {
  if (MSA_FAMILY_TYPES.has(type)) return 'MSA'
  if (MSA_HOUSING_FAMILY_TYPES.has(type)) return 'MSA - Housing'
  if (CSA_FAMILY_TYPES.has(type)) return 'CSA'
  return null
}

/**
 * Normalize deal type for versioning.
 * MSA / RMSA → "MSA", MSA - Housing / RMSA - Housing → "MSA - Housing"
 */
export function normalizeTypeForVersioning(type: string): string {
  if (MSA_FAMILY_TYPES.has(type)) return 'MSA'
  if (MSA_HOUSING_FAMILY_TYPES.has(type)) return 'MSA - Housing'
  return type
}

/**
 * Check if a deal is currently active (won and not ended/cancelled/replaced).
 */
export function isDealActive(deal: NewDeal): boolean {
  const status = deal?.dealStatus ?? ''
  return deal?.stage === 'Deal Won' && !TERMINAL_STATUSES.has(status)
}

/**
 * Sort deals by lineage chain (predecessorId/successorId) when available,
 * falling back to date-based sorting for legacy deals without chain links.
 *
 * Chain sorting: find the root deal (masterAgreementId match or no predecessorId),
 * then walk successorId links to produce the correct order regardless of dates.
 */
function sortDealsForVersioning(deals: NewDeal[]): NewDeal[] {
  if (deals.length <= 1) return [...deals]

  // Try chain-based sorting using predecessorId/successorId
  const byId = new Map(deals.map(d => [d.deal_uuid, d]))
  const hasChainLinks = deals.some(d => d.predecessorId || d.successorId)

  if (hasChainLinks) {
    // Find the root: deal whose deal_uuid matches the masterAgreementId,
    // or the deal with no predecessorId (or whose predecessorId isn't in this group)
    const root =
      deals.find(d => d.deal_uuid === d.masterAgreementId && !byId.has(d.predecessorId ?? '')) ||
      deals.find(d => !d.predecessorId || !byId.has(d.predecessorId)) ||
      deals[0]

    // Walk the successor chain from root
    const sorted: NewDeal[] = []
    const visited = new Set<string>()
    let current: NewDeal | undefined = root

    while (current && !visited.has(current.deal_uuid)) {
      visited.add(current.deal_uuid)
      sorted.push(current)
      current = current.successorId ? byId.get(current.successorId) : undefined
    }

    // Append any deals not reachable via the chain (orphans / broken links)
    // sorted by date as fallback
    const orphans = deals
      .filter(d => !visited.has(d.deal_uuid))
      .sort((a, b) => {
        const aDate = new Date(a.startDate || a.lastModified || 0).getTime()
        const bDate = new Date(b.startDate || b.lastModified || 0).getTime()
        return aDate - bDate
      })

    return [...sorted, ...orphans]
  }

  // Legacy fallback: sort by startDate with active deals pushed to the end
  return [...deals].sort((a, b) => {
    const aActive = isDealActive(a)
    const bActive = isDealActive(b)
    if (aActive && !bActive) return 1
    if (!aActive && bActive) return -1
    const aDate = new Date(a.startDate || a.lastModified || 0).getTime()
    const bDate = new Date(b.startDate || b.lastModified || 0).getTime()
    return aDate - bDate
  })
}

/**
 * Assign cumulative semantic versions:
 * - Major increments for each root type (MSA, CSA)
 * - Minor increments for each revision type (RMSA, RCSA)
 * E.g. 3 MSAs + 5 RMSAs = version 3.5.0
 */
function assignCumulativeVersions(
  sortedDeals: NewDeal[],
  family: AgreementFamily
): string[] {
  const rootSet = ROOT_TYPES[family]
  let major = 0
  let minor = 0

  return sortedDeals.map(deal => {
    if (rootSet.has(deal.type)) {
      major++
    } else {
      minor++
    }
    return `${major}.${minor}.0`
  })
}

// ─── Core Grouping Logic ──────────────────────────────────────────────

/**
 * Build a single MasterAgreement from a group of deals.
 */
function buildAgreement(
  groupDeals: NewDeal[],
  family: AgreementFamily,
  maId: string,
  productsByDealId: Map<string, Product[]>
): MasterAgreement {
  const sorted = sortDealsForVersioning(groupDeals)
  const versionLabels = assignCumulativeVersions(sorted, family)
  const rootSet = ROOT_TYPES[family]
  const firstRoot = sorted.find(d => rootSet.has(d.type)) ?? sorted[0]

  const versions: VersionedDeal[] = sorted.map((deal, index) => ({
    deal,
    semanticVersion: deal.semanticVersion || versionLabels[index],
    masterAgreementId: maId,
    isRoot: rootSet.has(deal.type),
    products: productsByDealId.get(deal.deal_uuid) ?? [],
  }))

  return {
    masterAgreementId: maId,
    rootDeal: firstRoot,
    agreementFamily: family,
    currentVersion: versions[versions.length - 1],
    versions,
  }
}

/**
 * Groups versionable deals into Master Agreements. IOs remain standalone.
 *
 * Grouping strategy:
 * 1. Deals WITH a stored masterAgreementId are grouped by that ID.
 *    Root deals whose deal_uuid matches a chain's masterAgreementId
 *    are merged into that chain (even if they lack the field themselves).
 * 2. Remaining deals WITHOUT masterAgreementId are grouped by family
 *    type (MSA, MSA-Housing, CSA) using the legacy cumulative approach.
 *
 * Within each group, deals are sorted chronologically with active deals last.
 */
export function buildMasterAgreements(
  deals: NewDeal[],
  products: Product[]
): { agreements: MasterAgreement[]; standaloneDeals: NewDeal[] } {
  const standaloneDeals: NewDeal[] = []

  // Build product lookup by deal_uuid
  const productsByDealId = new Map<string, Product[]>()
  for (const p of products) {
    const id = p?.deal_uuid ?? ''
    if (!id) continue
    const arr = productsByDealId.get(id) ?? []
    arr.push(p)
    productsByDealId.set(id, arr)
  }

  // Step 1: Separate deals into explicit chains vs unchained
  const chainsByMaId = new Map<string, NewDeal[]>()
  const unchained: NewDeal[] = []

  for (const deal of deals) {
    const family = getAgreementFamily(deal?.type ?? '')
    if (!family) {
      standaloneDeals.push(deal)
      continue
    }

    if (deal.masterAgreementId) {
      const chain = chainsByMaId.get(deal.masterAgreementId) ?? []
      chain.push(deal)
      chainsByMaId.set(deal.masterAgreementId, chain)
    } else {
      unchained.push(deal)
    }
  }

  // Step 2: Merge unchained root deals whose UUID matches a chain's masterAgreementId
  // (root deals created before the versioning system may lack masterAgreementId)
  const remaining: NewDeal[] = []
  for (const deal of unchained) {
    if (chainsByMaId.has(deal.deal_uuid)) {
      chainsByMaId.get(deal.deal_uuid)!.push(deal)
    } else {
      remaining.push(deal)
    }
  }

  // Step 3: Build agreements from explicit chains
  const agreements: MasterAgreement[] = []

  for (const [maId, chainDeals] of chainsByMaId) {
    const family = getAgreementFamily(chainDeals[0]?.type ?? '') ?? 'MSA'
    agreements.push(buildAgreement(chainDeals, family, maId, productsByDealId))
  }

  // Step 4: Build agreements from remaining unchained deals (legacy grouping by family)
  const familyBuckets: Record<AgreementFamily, NewDeal[]> = {
    MSA: [],
    'MSA - Housing': [],
    CSA: [],
  }
  for (const deal of remaining) {
    const family = getAgreementFamily(deal?.type ?? '')
    if (family) familyBuckets[family].push(deal)
  }

  for (const family of ['MSA', 'MSA - Housing', 'CSA'] as AgreementFamily[]) {
    const bucket = familyBuckets[family]
    if (bucket.length === 0) continue
    const rootSet = ROOT_TYPES[family]
    const sorted = sortDealsForVersioning(bucket)
    const firstRoot = sorted.find(d => rootSet.has(d.type)) ?? sorted[0]
    agreements.push(buildAgreement(bucket, family, firstRoot.deal_uuid, productsByDealId))
  }

  return { agreements, standaloneDeals }
}

// ─── Version Calculation ─────────────────────────────────────────────

/**
 * Parse a semantic version string into its numeric parts.
 */
export function parseSemanticVersion(version: string): { major: number; minor: number; patch: number } {
  const parts = (version || '0.0.0').split('.').map(Number)
  return {
    major: parts[0] || 0,
    minor: parts[1] || 0,
    patch: parts[2] || 0,
  }
}

/**
 * Calculate the next semantic version given the current version and change type.
 * - major: new full contract → X+1.0.0
 * - minor: product additions/removals → X.Y+1.0
 * - patch: budget/pricing changes only → X.Y.Z+1
 */
export function calculateNextVersion(currentVersion: string, changeType: VersionChangeType): string {
  const { major, minor, patch } = parseSemanticVersion(currentVersion)
  switch (changeType) {
    case 'major':
      return `${major + 1}.0.0`
    case 'minor':
      return `${major}.${minor + 1}.0`
    case 'patch':
      return `${major}.${minor}.${patch + 1}`
    default:
      return currentVersion
  }
}

/**
 * Find the highest semantic version across all versions in an agreement.
 * Used to ensure new versions don't collide with existing ones.
 */
export function getHighestVersion(agreement: MasterAgreement): string {
  let maxMajor = 0, maxMinor = 0, maxPatch = 0
  for (const v of agreement.versions) {
    const { major, minor, patch } = parseSemanticVersion(v.semanticVersion)
    if (
      major > maxMajor ||
      (major === maxMajor && minor > maxMinor) ||
      (major === maxMajor && minor === maxMinor && patch > maxPatch)
    ) {
      maxMajor = major
      maxMinor = minor
      maxPatch = patch
    }
  }
  return `${maxMajor}.${maxMinor}.${maxPatch}`
}

/**
 * Detect active MSA or MSA-Housing master agreements for an org's deals.
 * Returns only MSA and MSA-Housing families (CSA excluded from versioning flow).
 */
export function detectVersionableAgreements(
  orgDeals: NewDeal[],
  orgProducts: Product[]
): MasterAgreement[] {
  const { agreements } = buildMasterAgreements(orgDeals, orgProducts)
  return agreements.filter(
    a => (a.agreementFamily === 'MSA' || a.agreementFamily === 'MSA - Housing') &&
      a.versions.some(v => isDealActive(v.deal))
  )
}

/**
 * Get all active versions within a MasterAgreement.
 * Pre-versioning data may have multiple active deals in the same family.
 */
export function getActiveVersions(agreement: MasterAgreement): VersionedDeal[] {
  return agreement.versions.filter(v => isDealActive(v.deal))
}

/**
 * Convert a semantic version string "1.0.1" to a zero-padded
 * versionKey "v001.000.001" suitable for lexicographic DynamoDB GSI sorting.
 */
export function toVersionKey(semanticVersion: string): string {
  const { major, minor, patch } = parseSemanticVersion(semanticVersion)
  const pad = (n: number) => String(n).padStart(3, '0')
  return `v${pad(major)}.${pad(minor)}.${pad(patch)}`
}
